import { useMemo, useState } from 'react';
import { Check, ExternalLink, Inbox } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { FIRST_CRITERION_ITEM, domainNameItem } from '@/components/health-readiness/model';
import { useEngagementQuery } from '@/lib/api/engagements';
import {
  useMarkAllReadMutation,
  useMarkReadMutation,
  useNotificationsInboxQuery,
  type NotificationItem,
} from '@/lib/api/notifications';
import { formatDate } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';

/**
 * Notification inbox container — top-level component for `/notifications`
 * route. C20-130 replaces the prior grouped-by-severity layout with the
 * flat tabbed list per the prototype contract in
 * `design_handoff_caliber_v2/prototype/screens-portfolio.js → Screens
 * .notifications`:
 *
 *   ┌─ Card ────────────────────────────────────────────────────────────┐
 *   │ [Unread · N] [All]                       [Mark all read]          │
 *   ├───────────────────────────────────────────────────────────────────┤
 *   │ ● Engagement · event text                          12 min · Open  │
 *   │ ● Engagement · event text                          4h     · Open  │
 *   │ ...                                                               │
 *   └───────────────────────────────────────────────────────────────────┘
 *
 * Visual contract details:
 *   - 8px posture-coloured dot on the left (critical/at-risk/ai-surface
 *     for AI events / unassessed for info)
 *   - Engagement name bold; event summary follows
 *   - Relative timestamp meta line
 *   - "Open" button on the right deep-links to the engagement sub-route
 *     mapped from `event_type` (see EVENT_TYPE_TO_PATH below)
 *
 * Mark-all-read: backend has no bulk endpoint today (flagged backend gap)
 * — we fan out POST /:id/read for every unread row.
 */

type Filter = 'unread' | 'all';

/**
 * Deep-link mapping from `event_type` → engagement sub-route. Items with
 * no entry here fall back to `source_link` (if present) or the engagement
 * overview. The taxonomy is owned by
 * `packages/notification-fanout/src/taxonomy.ts`; expand here when new
 * types ship.
 */
/**
 * Health & Readiness with a rail row (E20): the old Health Check and Go-Live
 * URLs only redirect there, so the inbox links to the screen directly.
 */
function healthReadiness(engagementId: string, item?: string): string {
  const path = `/engagements/${engagementId}/health-readiness`;
  return item ? `${path}?item=${encodeURIComponent(item)}` : path;
}

const EVENT_TYPE_TO_PATH: Record<string, (engagementId: string) => string> = {
  'finding.severity_crossed': (id) => healthReadiness(id, 'findings'),
  'finding.created': (id) => healthReadiness(id, 'findings'),
  // Deviations live under Contract Compliance (this used to open Health
  // Check findings, which is a different register).
  'deviation.logged': (id) => `/engagements/${id}/contract/deviations`,
  'deviation.escalated': (id) => `/engagements/${id}/contract/deviations`,
  'deviation.severity_changed': (id) => healthReadiness(id, 'findings'),
  'rfi.created': (id) => `/engagements/${id}/rfis`,
  'rfi.responded': (id) => `/engagements/${id}/rfis`,
  'rfi.overdue': (id) => `/engagements/${id}/rfis`,
  'review.submitted': (id) => `/engagements/${id}/documents/review`,
  'review.decided': (id) => `/engagements/${id}/documents/review`,
  'proposals.ready': (id) => `/engagements/${id}/documents/review`,
  'proposal.ready': (id) => `/engagements/${id}/documents/review`,
  'snapshot.captured': (id) => `/engagements/${id}/archive`,
  'pulse.published': (id) => `/engagements/${id}/contract/pulse`,
  'posture.changed': (id) => healthReadiness(id),
  'deliverable.overdue': (id) => `/engagements/${id}/contract/deliverables`,
  'deliverable.rejected': (id) => `/engagements/${id}/contract/deliverables`,
  'register.changed': (id) => healthReadiness(id, 'findings'),
  'routing.decided': (id) => `/engagements/${id}/documents/review`,
  'engagement.invited': (id) => `/engagements/${id}`,
  'checkpoint.scored': (id) => `/engagements/${id}/contract/vendor`,
  'score.threshold_crossed': (id) => healthReadiness(id, FIRST_CRITERION_ITEM),
  'readiness.report_published': (id) => healthReadiness(id, 'report'),
  'milestone.slipped': (id) => healthReadiness(id, domainNameItem('Schedule')),
  // The one Health & Readiness report publishes this (E20/E22); it opens the Report tab.
  'synthesis.published': (id) => healthReadiness(id, 'report'),
  'document.parsed': (id) => `/engagements/${id}/documents`,
  'document.indexed': (id) => `/engagements/${id}/documents`,
  'routing.requested': (id) => `/engagements/${id}/documents/review`,
  'reeval.flagged': (id) => `/engagements/${id}/documents/review`,
  'override.captured': (id) => `/engagements/${id}/audit`,
  'domain.rating_changed': (id) => healthReadiness(id),
};

/**
 * T-0157 — events about firm content (the requirement library) carry the
 * all-zero firm-scope id instead of an engagement. They are labelled by
 * area, and open their own `source_link`.
 */
const FIRM_SCOPE_ID = '00000000-0000-0000-0000-000000000000';

const FIRM_EVENT_AREA: Record<string, string> = {
  'library_intake.failed': 'Requirement library',
  'library_intake.extracted': 'Requirement library',
};

export function NotificationInbox() {
  const [filter, setFilter] = useState<Filter>('unread');
  const inboxQuery = useNotificationsInboxQuery();
  const markRead = useMarkReadMutation();
  const markAll = useMarkAllReadMutation();

  const items = inboxQuery.data?.items ?? [];

  const unreadCount = useMemo(() => items.filter((i) => !i.is_read).length, [items]);

  const visible = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter((i) => !i.is_read);
  }, [items, filter]);

  function markAllRead() {
    markAll.mutate();
  }

  if (inboxQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading notifications…
      </div>
    );
  }
  if (inboxQuery.error) {
    return <ErrorMessage error={inboxQuery.error} />;
  }

  const isEmpty = items.length === 0;

  return (
    <div className="mx-auto w-full max-w-[920px] space-y-5">
      <header>
        <h1 className="text-display font-semibold text-ink-900">Notifications</h1>
        <p className="mt-1 text-meta text-ink-500">
          Red-flag and AI-proposal events across your engagements.{' '}
          <Link
            to="/settings/my-notifications"
            className="text-ink-700 underline-offset-2 hover:underline"
          >
            Configure preferences in firm settings.
          </Link>
        </p>
      </header>

      <section className="overflow-hidden rounded-card border border-ink-100 bg-paper-0">
        <div className="flex items-center gap-4 border-b border-ink-100 px-5 py-3">
          <TabButton active={filter === 'unread'} onClick={() => setFilter('unread')}>
            Unread <span className="font-mono text-ink-500">· {unreadCount}</span>
          </TabButton>
          <TabButton active={filter === 'all'} onClick={() => setFilter('all')}>
            All <span className="font-mono text-ink-500">· {items.length}</span>
          </TabButton>
          <button
            type="button"
            onClick={markAllRead}
            disabled={unreadCount === 0 || markRead.isPending}
            className={cn(
              'ml-auto inline-flex h-7 items-center gap-1 rounded-chip px-2.5 text-meta font-medium transition-colors',
              unreadCount > 0 && !markRead.isPending
                ? 'text-ink-700 hover:text-ink-900'
                : 'text-ink-300',
              'disabled:opacity-60',
            )}
          >
            <Check className="h-3 w-3" aria-hidden="true" />
            Mark all read
          </button>
        </div>

        {isEmpty ? (
          <div className="p-5">
            <EmptyState
              title="All caught up."
              description="No notifications right now. New red-flag or AI-proposal events will land here."
              action={<Inbox className="h-10 w-10 text-ink-300" aria-hidden="true" />}
            />
          </div>
        ) : visible.length === 0 ? (
          <div className="p-5 text-meta text-ink-500">No unread notifications.</div>
        ) : (
          <ul className="divide-y divide-ink-100">
            {visible.map((item) => (
              <li key={item.id}>
                <NotificationRow
                  item={item}
                  onMarkRead={(id) => markRead.mutate(id)}
                  markPending={markRead.isPending}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function TabButton({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1 rounded-none border-b-2 pb-1.5 pt-0.5 text-body-sm font-medium transition-colors',
        active
          ? 'border-accent-amber-600 text-ink-900'
          : 'border-transparent text-ink-500 hover:text-ink-700',
      )}
    >
      {children}
    </button>
  );
}

function NotificationRow({
  item,
  onMarkRead,
  markPending,
}: {
  item: NotificationItem;
  onMarkRead: (id: string) => void;
  markPending: boolean;
}) {
  const firmScope = item.engagement_id === FIRM_SCOPE_ID;
  const engagement = useEngagementQuery(firmScope ? undefined : item.engagement_id);
  const engagementName = firmScope
    ? (FIRM_EVENT_AREA[item.event_type] ?? 'Firm')
    : (engagement.data?.name ?? engagement.data?.project_name ?? '…');
  const dotColor = dotColorVar(item.severity, item.event_type);
  const targetPath = firmScope
    ? item.source_link || '/settings'
    : (EVENT_TYPE_TO_PATH[item.event_type]?.(item.engagement_id) ??
      item.source_link ??
      `/engagements/${item.engagement_id}`);

  return (
    <div
      className="flex items-start gap-3 px-5 py-3.5"
      data-component="notification-row"
      data-read={item.is_read ? 'true' : 'false'}
    >
      <span
        aria-hidden="true"
        className="mt-1.5 inline-block h-2 w-2 flex-none rounded-full"
        style={{ background: `var(${dotColor})` }}
      />
      <div className="min-w-0 flex-1">
        <div
          className={cn('text-body-sm leading-5', item.is_read ? 'text-ink-500' : 'text-ink-700')}
        >
          <span className={cn('font-semibold', item.is_read ? 'text-ink-500' : 'text-ink-900')}>
            {engagementName}
          </span>
          <span className="text-ink-500"> · </span>
          <span title={item.summary}>{item.summary || item.title}</span>
        </div>
        <div className="mt-0.5 text-meta text-ink-500">{formatRelative(item.created_at)}</div>
      </div>
      <div className="ml-auto flex flex-none items-center gap-2">
        {item.is_read ? null : (
          <button
            type="button"
            onClick={() => onMarkRead(item.id)}
            disabled={markPending}
            className="inline-flex h-7 items-center gap-1 rounded-chip border border-ink-100 px-2 text-meta font-medium text-ink-700 hover:border-ink-300 hover:text-ink-900 disabled:opacity-50"
            aria-label="Mark notification as read"
          >
            <Check className="h-3 w-3" aria-hidden="true" />
            Mark read
          </button>
        )}
        <a
          href={targetPath}
          className="inline-flex h-7 items-center gap-1 rounded-chip border border-ink-200 bg-paper-0 px-2.5 text-meta font-medium text-ink-700 hover:border-ink-300 hover:text-ink-900"
        >
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
          Open
        </a>
      </div>
    </div>
  );
}

function dotColorVar(severity: NotificationItem['severity'], eventType: string): string {
  if (eventType.startsWith('proposal') || eventType.startsWith('proposals.')) {
    return '--ai-surface-border';
  }
  switch (severity) {
    case 'critical':
    case 'high':
      return '--posture-critical';
    case 'warning':
      return '--posture-at-risk';
    case 'info':
    default:
      return '--posture-unassessed';
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return formatDate(iso);
}
